import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle, BellRing, CalendarCheck, CheckCircle2, FileSignature, Loader2, Lock, MessageSquareText, Plug, Send, Sparkles, Unplug,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useToast } from '@/hooks/use-toast';
import {
  SmsActionError,
  useConnectSms,
  useDisconnectSms,
  useSendTestSms,
  useSmsLog,
  useSmsSettings,
  useUpdateSmsPreferences,
  type SmsSettings,
} from '@/hooks/useSmsSettings';
import {
  SMS_TYPE_LABEL,
  accountSidProblem,
  authTokenProblem,
  isSmsFailure,
  normaliseUkMobile,
  parseSmsSender,
  phoneProblem,
  senderLabel,
  smsStatusLabel,
} from '@/lib/sms';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminPage, EmptyState, IconTile, PageHeader, Panel, SettingRow, StatusChip, adminButton, useAdminPageTitle } from '@/components/admin/ui';

const errorText = (err: unknown) =>
  err instanceof Error && err.message ? err.message : 'Something went wrong. Please try again.';

const SENDER_HELP: Record<string, string> = {
  phone: 'A number you’ve bought in Twilio, like +447700900123. Customers can reply to it.',
  messaging_service: 'Starts with MG. Find it in Twilio under Messaging → Services.',
  alphanumeric: 'Up to 11 letters or numbers, like SparkleCo. UK networks show it instead of a number, but customers can’t reply.',
};

const SENDER_PLACEHOLDER: Record<string, string> = {
  phone: '+447700900123',
  messaging_service: 'MG…',
  alphanumeric: 'SparkleCo',
};

const connectSchema = z
  .object({
    accountSid: z.string().trim(),
    authToken: z.string().trim(),
    senderKind: z.enum(['phone', 'messaging_service', 'alphanumeric']),
    senderValue: z.string().trim(),
  })
  .superRefine((v, ctx) => {
    const sid = accountSidProblem(v.accountSid);
    if (sid) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['accountSid'], message: sid });
    const token = authTokenProblem(v.authToken);
    if (token) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['authToken'], message: token });
    const sender = parseSmsSender(v.senderKind, v.senderValue);
    if (!sender.ok) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['senderValue'], message: sender.message });
  });
type ConnectValues = z.infer<typeof connectSchema>;

const testSchema = z.object({
  to: z.string().superRefine((value, ctx) => {
    const problem = phoneProblem(normaliseUkMobile(value));
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  }),
});
type TestValues = z.infer<typeof testSchema>;

function UpgradePanel({ connected }: { connected: boolean }) {
  return (
    <Panel data-testid="sms-upgrade">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <IconTile tone="violet" className="h-11 w-11 rounded-xl [&_svg]:h-5 [&_svg]:w-5"><Lock /></IconTile>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-foreground">Text messages are on Team and Business</p>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {connected
              ? 'Your Twilio account is still connected, but texts are paused on your current plan. Upgrade to switch them back on.'
              : 'Text your customers booking confirmations, reminders and quote links from your own Twilio account.'}
          </p>
        </div>
        <Button asChild size="sm" className={adminButton.primary}>
          <Link to="/admin/billing"><Sparkles /> See plans</Link>
        </Button>
      </div>
    </Panel>
  );
}

function ConnectForm({ orgId, disabled }: { orgId: string; disabled: boolean }) {
  const { toast } = useToast();
  const connect = useConnectSms(orgId);
  const [problem, setProblem] = useState<string | null>(null);
  const form = useForm<ConnectValues>({
    resolver: zodResolver(connectSchema),
    defaultValues: { accountSid: '', authToken: '', senderKind: 'phone', senderValue: '' },
  });
  const kind = form.watch('senderKind');

  const submit = form.handleSubmit(async (values) => {
    setProblem(null);
    const sender = parseSmsSender(values.senderKind, values.senderValue);
    if (!sender.ok) return;
    try {
      const res = await connect.mutateAsync({
        accountSid: values.accountSid.trim(),
        authToken: values.authToken.trim(),
        senderKind: values.senderKind,
        senderValue: sender.value,
      });
      form.reset({ accountSid: '', authToken: '', senderKind: values.senderKind, senderValue: '' });
      toast({
        title: 'Twilio connected',
        description: res.trialAccount
          ? 'This is a Twilio trial account, so it can only text numbers you’ve verified in Twilio.'
          : 'Send yourself a test text to check it works.',
      });
    } catch (err) {
      setProblem(errorText(err));
    }
  });

  return (
    <Panel
      title="Connect your Twilio account"
      icon={<Plug className="h-4 w-4 text-muted-foreground" />}
      description="Texts go from your own Twilio account, so Twilio bills you directly. We never charge for texts."
      data-testid="sms-connect"
    >
      <Form {...form}>
        <form onSubmit={(e) => void submit(e)} className="space-y-4" noValidate>
          <p className="text-[12.5px] text-muted-foreground">
            Sign in at console.twilio.com. Your Account SID and Auth Token are on the home page.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="accountSid"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account SID</FormLabel>
                  <FormControl>
                    <Input placeholder="AC…" autoComplete="off" spellCheck={false} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="authToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Auth Token</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" spellCheck={false} {...field} />
                  </FormControl>
                  <FormDescription>Stored securely. You won’t see it again here.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <FormField
              control={form.control}
              name="senderKind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Send texts from</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger aria-label="Send texts from"><SelectValue /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="phone">A Twilio phone number</SelectItem>
                      <SelectItem value="messaging_service">A Messaging Service</SelectItem>
                      <SelectItem value="alphanumeric">Your business name</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="senderValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {kind === 'phone' ? 'Twilio phone number' : kind === 'messaging_service' ? 'Messaging Service SID' : 'Sender name'}
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={SENDER_PLACEHOLDER[kind]} autoComplete="off" spellCheck={false} {...field} />
                  </FormControl>
                  <FormDescription>{SENDER_HELP[kind]}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {problem && (
            <p className="flex items-start gap-2 text-[13px] text-destructive" role="alert" data-testid="sms-connect-error">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{problem}</span>
            </p>
          )}
          <Button type="submit" className={adminButton.primary} disabled={connect.isPending || disabled}>
            {connect.isPending ? <Loader2 className="animate-spin" /> : <Plug />}
            {connect.isPending ? 'Checking with Twilio…' : 'Connect Twilio'}
          </Button>
        </form>
      </Form>
    </Panel>
  );
}

function TestTextForm({ orgId, disabled }: { orgId: string; disabled: boolean }) {
  const { toast } = useToast();
  const sendTest = useSendTestSms(orgId);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const form = useForm<TestValues>({ resolver: zodResolver(testSchema), defaultValues: { to: '' } });

  const submit = form.handleSubmit(async ({ to }) => {
    setResult(null);
    const phone = normaliseUkMobile(to);
    if (!phone.ok) return;
    try {
      await sendTest.mutateAsync(phone.e164);
      setResult({ ok: true, text: `Test text sent to ${phone.e164}. It should arrive in a few seconds.` });
      toast({ title: 'Test text sent' });
    } catch (err) {
      setResult({ ok: false, text: errorText(err) });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={(e) => void submit(e)} className="space-y-2" noValidate data-testid="sms-test-form">
        <FormField
          control={form.control}
          name="to"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Send a test text to your mobile</FormLabel>
              <div className="flex flex-col gap-2 sm:flex-row">
                <FormControl>
                  <Input type="tel" inputMode="tel" placeholder="07700 900123" autoComplete="tel" {...field} />
                </FormControl>
                <Button type="submit" variant="outline" className={adminButton.outline} disabled={sendTest.isPending || disabled}>
                  {sendTest.isPending ? <Loader2 className="animate-spin" /> : <Send />}
                  Send a test text
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        {result && (
          <p
            className={result.ok ? 'text-[13px] text-green-700 dark:text-green-400' : 'text-[13px] text-destructive'}
            role={result.ok ? 'status' : 'alert'}
            data-testid="sms-test-result"
          >
            {result.text}
          </p>
        )}
      </form>
    </Form>
  );
}

function ConnectedPanel({ orgId, settings, canWrite, onDisconnect }: {
  orgId: string;
  settings: SmsSettings;
  canWrite: boolean;
  onDisconnect: () => void;
}) {
  return (
    <Panel
      title="Twilio"
      icon={<MessageSquareText className="h-4 w-4 text-muted-foreground" />}
      actions={
        <StatusChip tone={settings.planAllows ? 'green' : 'amber'} data-testid="sms-connected-chip">
          {settings.planAllows ? 'Connected' : 'Paused'} · AC…{settings.accountSidLast4}
        </StatusChip>
      }
      data-testid="sms-connected"
    >
      <div className="space-y-4">
        <dl className="grid gap-3 text-[13px] sm:grid-cols-2">
          {settings.accountName && (
            <div>
              <dt className="text-[11.5px] font-medium text-muted-foreground">Twilio account</dt>
              <dd className="font-medium text-foreground">{settings.accountName}</dd>
            </div>
          )}
          <div>
            <dt className="text-[11.5px] font-medium text-muted-foreground">Texts come from</dt>
            <dd className="break-all font-medium text-foreground" data-testid="sms-sender">
              {senderLabel(settings.senderKind, settings.senderValue)}
            </dd>
          </div>
        </dl>
        {settings.senderKind === 'alphanumeric' && (
          <p className="text-[12.5px] text-muted-foreground">Customers can’t reply to a sender name, so texts include your phone number when it’s set in Company Info.</p>
        )}
        {settings.planAllows && <TestTextForm orgId={orgId} disabled={!canWrite} />}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <p className="text-[12.5px] text-muted-foreground">Twilio bills you directly for each text.</p>
          <Button type="button" size="sm" variant="outline" className={adminButton.danger} onClick={onDisconnect} disabled={!canWrite}>
            <Unplug /> Disconnect
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function PreferencesPanel({ orgId, settings, canWrite }: { orgId: string; settings: SmsSettings; canWrite: boolean }) {
  const { toast } = useToast();
  const update = useUpdateSmsPreferences(orgId);
  const prefs = {
    sendBookingConfirmation: settings.sendBookingConfirmation,
    sendAppointmentReminder: settings.sendAppointmentReminder,
    sendQuoteLink: settings.sendQuoteLink,
  };
  const set = (key: keyof typeof prefs) => (value: boolean) => {
    update.mutate({ ...prefs, [key]: value }, {
      onError: (err) => toast({ title: 'Couldn’t save that', description: errorText(err), variant: 'destructive' }),
    });
  };
  const rows: { key: keyof typeof prefs; id: string; icon: JSX.Element; title: string; description: string }[] = [
    { key: 'sendBookingConfirmation', id: 'sms-booking', icon: <CalendarCheck />, title: 'Booking confirmations', description: 'When a booking is confirmed, straight away or when you approve it.' },
    { key: 'sendAppointmentReminder', id: 'sms-reminder', icon: <BellRing />, title: 'Appointment reminders', description: 'The day before, alongside the reminder email.' },
    { key: 'sendQuoteLink', id: 'sms-quote', icon: <FileSignature />, title: 'Quote links', description: 'When you send a quote, with a link to view and accept it.' },
  ];
  return (
    <Panel title="What gets texted" description="Texts go to customers with a UK mobile number who haven’t opted out." data-testid="sms-preferences">
      <div className="divide-y divide-border">
        {rows.map((row) => (
          <SettingRow
            key={row.key}
            icon={row.icon}
            title={row.title}
            description={row.description}
            htmlFor={row.id}
            control={
              <Switch
                id={row.id}
                checked={prefs[row.key]}
                onCheckedChange={set(row.key)}
                disabled={!canWrite || !settings.planAllows}
                aria-label={row.title}
              />
            }
          />
        ))}
      </div>
    </Panel>
  );
}

function LogPanel({ orgId }: { orgId: string }) {
  const { data: log = [], isLoading } = useSmsLog(orgId);
  const failures = log.filter((m) => isSmsFailure(m.status)).length;
  return (
    <Panel
      title="Recent texts"
      actions={failures > 0 ? <StatusChip tone="rose" data-testid="sms-failure-count">{failures} failed</StatusChip> : undefined}
      data-testid="sms-log"
    >
      {isLoading ? (
        <div className="flex h-16 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : log.length === 0 ? (
        <EmptyState compact icon={<MessageSquareText />} title="No texts yet" description="Texts you send will show here with their delivery status." />
      ) : (
        <ul className="divide-y divide-border">
          {log.map((m) => {
            const status = smsStatusLabel(m.status);
            return (
              <li key={m.id} className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-start sm:justify-between" data-testid="sms-log-row">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-foreground">
                    {SMS_TYPE_LABEL[m.message_type] ?? m.message_type}
                    <span className="adm-num ml-2 font-normal text-muted-foreground">{m.to_masked}</span>
                  </p>
                  {m.error_message && isSmsFailure(m.status) && (
                    <p className="text-[12.5px] text-destructive">{m.error_message}</p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-[12px] text-muted-foreground">
                    {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                  </span>
                  <StatusChip tone={status.tone}>{status.label}</StatusChip>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

const SmsAdmin = () => {
  useAdminPageTitle('Text messages');
  const { orgId } = useAdminOrg();
  const { currentOrgRole, staffRole, isPlatformAdmin } = useAuth();
  const { canWrite, assertCanWrite } = useReadOnlyGuard();
  const { toast } = useToast();
  const isOwner = isPlatformAdmin || (currentOrgRole ? currentOrgRole === 'owner' : staffRole === 'owner');
  const { data: settings, isLoading, isError, error } = useSmsSettings(isOwner ? orgId : null);
  const disconnect = useDisconnectSms(orgId);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const handleDisconnect = async () => {
    if (!assertCanWrite()) return;
    try {
      await disconnect.mutateAsync();
      toast({ title: 'Twilio disconnected', description: 'We’ve deleted your Twilio details. No more texts will be sent.' });
    } catch (err) {
      toast({
        title: 'Couldn’t disconnect',
        description: err instanceof SmsActionError || err instanceof Error ? err.message : errorText(err),
        variant: 'destructive',
      });
    }
    setConfirmDisconnect(false);
  };

  if (!isOwner) {
    return (
      <AdminPage width="narrow" className="max-w-3xl">
        <PageHeader description="Text messages to your customers." />
        <Panel><p className="text-[13px] text-muted-foreground">Only the business owner can change text messages.</p></Panel>
      </AdminPage>
    );
  }

  if (isLoading || !orgId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <AdminPage width="narrow" className="max-w-3xl">
      <PageHeader description="Text customers their booking confirmations, reminders and quote links from your own Twilio account." />

      {isError && (
        <Panel>
          <p className="text-[13px] text-destructive">We couldn’t load your text message settings: {errorText(error)}</p>
        </Panel>
      )}

      {settings && !settings.planAllows && <UpgradePanel connected={settings.connected} />}

      {settings && settings.planAllows && !settings.connected && <ConnectForm orgId={orgId} disabled={!canWrite} />}

      {settings?.connected && (
        <>
          <ConnectedPanel orgId={orgId} settings={settings} canWrite={canWrite} onDisconnect={() => setConfirmDisconnect(true)} />
          <PreferencesPanel orgId={orgId} settings={settings} canWrite={canWrite} />
          <LogPanel orgId={orgId} />
        </>
      )}

      {settings && settings.planAllows && !settings.connected && (
        <Panel>
          <p className="flex items-start gap-2 text-[12.5px] text-muted-foreground">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Once connected, texts switch on for booking confirmations, appointment reminders and quote links. You can turn each one off.
          </p>
        </Panel>
      )}

      <AlertDialog open={confirmDisconnect} onOpenChange={(open) => !open && setConfirmDisconnect(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Twilio?</AlertDialogTitle>
            <AlertDialogDescription>
              We’ll stop sending texts and delete your Twilio details from The Polished Version. Your Twilio account
              itself isn’t affected, and you can connect again any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDisconnect();
              }}
              disabled={disconnect.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPage>
  );
};

export default SmsAdmin;
